import * as Sentry from '@sentry/nextjs';
import { envString, isLocal, isProd } from 'libs/env';
import type { NextPage } from 'next';
import type { AppProps as NextAppProps } from 'next/app';
import Head from 'next/head';
import { useRouter, withRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { ToastContainer } from 'react-toastify';
import CONFIG from 'const/config';
import { AppProviders } from '../app-providers';

/**
 * Import global styles, global css or polyfills here
 * i.e.: import '@/assets/theme/style.scss'
 */
import '../styles/global.scss';
import 'react-toastify/dist/ReactToastify.css';
import 'slim/lib/font-awesome/5.15.4/css/all.min.css';
import 'slim/lib/bootstrap/css/bootstrap.min.css';

// Start the mock worker for development
if (process.env.NEXT_PUBLIC_API_MOCKING === 'enabled') {
  require('mocks');
}

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactNode) => ReactNode;
};

// Workaround for https://github.com/zeit/next.js/issues/8592
export type AppPropsWithLayout = NextAppProps & {
  Component: NextPageWithLayout;
  err?: Error;
};

/**
 * @link https://nextjs.org/docs/advanced-features/custom-app
 */
const MyApp = ({ Component, pageProps, err }: AppPropsWithLayout) => {
  const router = useRouter();

  useEffect(() => {
    // Sentry
    const initSentry = (dsn: string) => {
      const local = isLocal();
      if (!local) {
        Sentry.init({
          dsn: dsn,
          enabled: true,
          environment: envString(),
          debug: !isProd(),
          initialScope: {
            tags: { app: 'user' },
          },
        });
      }
    };
    if (
      process.env.NEXT_PUBLIC_SENTRY_DSN &&
      process.env.NEXT_PUBLIC_SENTRY_DSN.length > 0
    ) {
      initSentry(process.env.NEXT_PUBLIC_SENTRY_DSN);
    }

    // GoogleTagManager
    const initTagManager = (tagManagerId: string) => {
      // タグマネージャー
      if (!isLocal() && (CONFIG.tagManagerDebug || isProd())) {
        TagManager.initialize({
          gtmId: tagManagerId,
        });
      }
    };
    if (
      process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID &&
      process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID.length > 0
    ) {
      initTagManager(process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID);
    }
  }, []);

  const getLayout =
    Component.getLayout ||
    ((page: ReactNode) => {
      return page;
    });

  const title = 'SIGNATE Cloud | 実務に直結するDX人材育成クラウドサービス';
  const description =
    'SIGNATE Cloudは、実務に直結するDX人材育成クラウドサービスです。DXに必要なデータリテラシー計測やスキルアップ講座、データ分析コンペティション等、DX人材育成に必要となる様々な要素を一つのツールで実現することができます。';
  return (
    <AppProviders>
      {!router.pathname.startsWith('/individual') && (
        <Head>
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta property="og:title" content={title} />
          <meta property="twitter:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="twitter:description" content={description} />
        </Head>
      )}
      {getLayout(<Component {...pageProps} err={err} />)}
      <ToastContainer />
    </AppProviders>
  );
};

/*
MyApp.getInitialProps = async appContext => {
   // calls page's `getInitialProps` and fills `appProps.pageProps`
   const appProps = await App.getInitialProps(appContext)
   return { ...appProps }
}
*/

export default withRouter(MyApp);
